import { useCallback, useEffect, useState } from "react";

import { getCochargeUserByEmaid, getCochargeUsersByEmail } from "../../services/owners";

export interface ResolvedUser {
  emaid: string | null;
  email: string | null;
  error: string | null;
  resolving: boolean;
}

const looksLikeEmail = (value: string) => value.includes("@");

/**
 * Both admin screens are keyed by eMAID but staff usually have an email, so either identifier is
 * accepted and the other is filled in from account management. Shared so the two screens cannot
 * disagree about what a valid person is.
 */
export const resolveToEmaid = async (
  entered: string,
): Promise<{ emaid: string | null; error: string | null }> => {
  const value = entered.trim();
  if (!value) return { emaid: null, error: null };
  if (!looksLikeEmail(value)) return { emaid: value, error: null };

  try {
    const users = await getCochargeUsersByEmail(value);
    const found = (users ?? []).find((u: any) => u?.emaid)?.emaid ?? null;
    return found
      ? { emaid: found, error: null }
      : { emaid: null, error: `No account found for ${value}` };
  } catch {
    return { emaid: null, error: `Could not look up ${value}` };
  }
};

/** Fills in whichever half of the identity the caller does not already have. */
export const useResolvedUser = (emaid: string | undefined): ResolvedUser => {
  const [email, setEmail] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!emaid) {
      setEmail(null);
      return;
    }
    const user = await getCochargeUserByEmaid(emaid).catch(() => null);
    setEmail(user?.email ?? null);
  }, [emaid]);

  useEffect(() => {
    void load();
  }, [load]);

  return { emaid: emaid ?? null, email, error: null, resolving: false };
};
